import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../helper/apiCall';
import ENDPOINTS from '../../constants/endpoints';

// Async thunks
export const getStudentsByAgeGroup = createAsyncThunk(
    'attendance/getStudentsByAgeGroup',
    async ({ ageGroupId, date, branchId }, thunkAPI) => {
        // Tüm parametreler mevcut olmalı
        if (!ageGroupId || !date || !branchId) {
            throw new Error('Gerekli parametreler eksik: ageGroupId, date, branchId');
        }

        try {
            const response = await client.get(`${ENDPOINTS.STUDENTS.GET_LIST_BY_AGE_GROUP}?ageGroupId=${ageGroupId}&date=${date}&branchId=${branchId}`, {
                token: thunkAPI.getState().auth.token
            });
            // API pagination formatında döndüğü için items array'ini alıyoruz
            return response.data.items || response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getStudentsByGroup = createAsyncThunk(
    'attendance/getStudentsByGroup',
    async ({ groupId, date, branchId }, thunkAPI) => {
        // Tüm parametreler mevcut olmalı
        if (!groupId || !date || !branchId) {
            throw new Error('Gerekli parametreler eksik: groupId, date, branchId');
        }

        try {
            const response = await client.get(`${ENDPOINTS.STUDENTS.GET_LIST_BY_GROUP}?groupId=${groupId}&date=${date}&branchId=${branchId}`, {
                token: thunkAPI.getState().auth.token
            });
            // API pagination formatında döndüğü için items array'ini alıyoruz
            return response.data.items || response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const addAttendance = createAsyncThunk(
    'attendance/addAttendance',
    async (attendanceData, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.STUDENT_ATTENDANCES.CREATE, attendanceData, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateAttendance = createAsyncThunk(
    'attendance/updateAttendance',
    async (attendanceData, thunkAPI) => {
        try {
            const response = await client.put(ENDPOINTS.STUDENT_ATTENDANCES.UPDATE, attendanceData, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Grup / yaş grubunun seçili gündeki yoklamasını toplu siler
export const deleteAttendancesByDate = createAsyncThunk(
    'attendance/deleteAttendancesByDate',
    async ({ branchId, date, groupId, ageGroupId }, thunkAPI) => {
        try {
            let url = `${ENDPOINTS.STUDENT_ATTENDANCES.DELETE_BY_DATE}?branchId=${branchId}&date=${date}`;
            if (groupId) {
                url += `&groupId=${groupId}`;
            }
            if (ageGroupId) {
                url += `&ageGroupId=${ageGroupId}`;
            }

            const response = await client.delete(url, undefined, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message || error);
        }
    }
);

// Grup / yaş grubunun yoklamasını fromDate'ten toDate'e taşır
export const moveAttendanceDate = createAsyncThunk(
    'attendance/moveAttendanceDate',
    async ({ branchId, fromDate, toDate, groupId, ageGroupId }, thunkAPI) => {
        const token = thunkAPI.getState().auth.token;
        try {
            // Hedef günde zaten yoklama varsa API reddeder; hata mesajı istemciye ulaşmadığı için burada kontrol ediyoruz
            const listUrl = groupId
                ? `${ENDPOINTS.STUDENTS.GET_LIST_BY_GROUP}?groupId=${groupId}&date=${toDate}&branchId=${branchId}`
                : `${ENDPOINTS.STUDENTS.GET_LIST_BY_AGE_GROUP}?ageGroupId=${ageGroupId}&date=${toDate}&branchId=${branchId}`;
            const listResponse = await client.get(listUrl, { token });
            const targetStudents = listResponse.data.items || listResponse.data || [];
            const conflictCount = targetStudents.filter(s => s.attendanceDate).length;
            if (conflictCount > 0) {
                return thunkAPI.rejectWithValue(
                    `Seçilen yeni tarihte ${conflictCount} öğrencinin zaten yoklaması var. Önce o günün yoklamasını silin.`
                );
            }

            const response = await client.put(ENDPOINTS.STUDENT_ATTENDANCES.MOVE_DATE, {
                branchId,
                fromDate,
                toDate,
                groupId: groupId || null,
                ageGroupId: ageGroupId || null
            }, { token });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message || error);
        }
    }
);

export const getAttendances = createAsyncThunk(
    'attendance/getAttendances',
    async ({ branchId, date, groupId, ageGroupId }, thunkAPI) => {
        // En azından tarih ve branch seçili olmalı
        if (!branchId || !date) {
            throw new Error('Gerekli parametreler eksik: branchId, date');
        }

        try {
            let url = `${ENDPOINTS.STUDENT_ATTENDANCES.GET_LIST}?branchId=${branchId}&date=${date}`;
            if (groupId) {
                url += `&groupId=${groupId}`;
            }
            if (ageGroupId) {
                url += `&ageGroupId=${ageGroupId}`;
            }
            
            const response = await client.get(url, {
                token: thunkAPI.getState().auth.token
            });
            // API pagination formatında döndüğü için items array'ini alıyoruz
            return response.data.items || response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    students: [],
    attendances: [],
    loading: false,
    error: null,
    selectedFilter: null // 'ageGroup' veya 'group'
};

const attendanceSlice = createSlice({
    name: 'attendance',
    initialState,
    reducers: {
        clearStudents: (state) => {
            state.students = [];
        },
        clearAttendances: (state) => {
            state.attendances = [];
        },
        setSelectedFilter: (state, action) => {
            state.selectedFilter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Get students by age group
        builder
            .addCase(getStudentsByAgeGroup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getStudentsByAgeGroup.fulfilled, (state, action) => {
                state.loading = false;
                state.students = action.payload;
                state.selectedFilter = 'ageGroup';
            })
            .addCase(getStudentsByAgeGroup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get students by group
            .addCase(getStudentsByGroup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getStudentsByGroup.fulfilled, (state, action) => {
                state.loading = false;
                state.students = action.payload;
                state.selectedFilter = 'group';
            })
            .addCase(getStudentsByGroup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add attendance
            .addCase(addAttendance.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addAttendance.fulfilled, (state, action) => {
                state.loading = false;
                // Yeni attendance'ı listeye ekle
            })
            .addCase(addAttendance.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update attendance
            .addCase(updateAttendance.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateAttendance.fulfilled, (state, action) => {
                state.loading = false;
                // Güncellenen attendance'ı listede güncelle
              
            })
            .addCase(updateAttendance.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get attendances
            .addCase(getAttendances.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAttendances.fulfilled, (state, action) => {
                state.loading = false;
                state.attendances = action.payload;
            })
            .addCase(getAttendances.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearStudents, clearAttendances, setSelectedFilter, clearError } = attendanceSlice.actions;
export default attendanceSlice.reducer; 